import React, { useMemo, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    UserCircleIcon,
    EnvelopeIcon,
    ChatBubbleLeftIcon,
    SparklesIcon,
    ArrowLeftEndOnRectangleIcon,
    ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { AiUsageData, getUsageBgColor } from "../../api/useAiUsage";
import { useConsumables } from "../../api/useConsumables";
import { useLogout } from "../../api/useLogout";
import { Facility } from "../../pages/settings/facilities/useFacility";
import { useStateContext } from "../../contexts/ContextProvider";
import UserLocaleSwitcher from "../general/UserLocaleSwitcher";
import { ACCOUNT_SECURITY_LINK } from "../../pages/settings/settingsMenu";

interface SidebarUserMenuProps {
    expanded: boolean;
    facility: Facility | null | undefined;
    aiUsage: AiUsageData | null | undefined;
}

export default function SidebarUserMenu({
    expanded,
    facility,
    aiUsage,
}: SidebarUserMenuProps) {
    const { user } = useStateContext();
    const { t } = useTranslation('navigation');
    const { data: consumables } = useConsumables();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { logout } = useLogout();

    // Fermer au clic extérieur
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    // `current_monthly_email_consumption` porte un SOLDE RESTANT malgré son
    // nom : il est initialisé au plafond puis décrémenté. La barre représente
    // donc ce qu'il reste — d'où des couleurs qui s'assombrissent quand le
    // pourcentage baisse, et non l'inverse.
    const emailRemainingPercentage = facility?.current_monthly_email_consumption && facility?.email_limit
        ? (facility.current_monthly_email_consumption / facility.email_limit) * 100
        : 0;

    const getEmailProgressColor = (remainingPct: number) => {
        if (remainingPct > 80) return "bg-green-500";
        if (remainingPct > 50) return "bg-yellow-500";
        return "bg-red-500";
    };

    // Pastille discrète : un consommable ENGAGÉ sous son seuil sans être épuisé.
    // L'épuisement, lui, est porté par le bandeau global — le poids visuel
    // suit la gravité (ADR 0005).
    //
    // `is_in_use` gate la pastille comme il gate le bandeau (ADR 0007) : un
    // solde bas sur un canal que l'établissement n'utilise pas est encore moins
    // justifiable qu'un solde vide. La pastille, elle, ne se met JAMAIS en
    // veille : c'est l'indicateur vers lequel le bandeau se replie, la masquer
    // aussi ne laisserait plus rien.
    const lowCount = useMemo(
        () => (consumables?.consumables ?? [])
            .filter((item) => item.level === 'low' && item.is_in_use)
            .length,
        [consumables],
    );

    /** Consommables que l'établissement ne dépense pas — affichés, jamais alarmants. */
    const unusedKeys = useMemo(
        () => new Set(
            (consumables?.consumables ?? [])
                .filter((item) => !item.is_in_use)
                .map((item) => item.key),
        ),
        [consumables],
    );

    // Calcul du pourcentage IA restant
    const aiRemainingPercentage = aiUsage
        ? Math.max(100 - aiUsage.percentage_used, 0)
        : 0;

    // Générer les initiales de l'utilisateur
    const getInitials = () => {
        const first = user?.firstname?.charAt(0)?.toUpperCase() || '';
        const last = user?.lastname?.charAt(0)?.toUpperCase() || '';
        return first + last || 'U';
    };

    return (
        <div className="relative px-2" ref={dropdownRef}>
            <button
                ref={buttonRef}
                onClick={handleToggle}
                className={`w-full flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-100 ${
                    expanded ? "justify-start" : "justify-center"
                }`}
            >
                {/* Avatar avec initiales */}
                <div className="relative w-9 h-9 flex-shrink-0">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-main-color-light to-main-color/20 flex items-center justify-center">
                        <span className="text-sm font-semibold text-main-color">
                            {getInitials()}
                        </span>
                    </div>
                    {lowCount > 0 && (
                        <span
                            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-white"
                            title={t('consumables.low_badge_tooltip', { count: lowCount })}
                        />
                    )}
                </div>

                {/* Infos utilisateur */}
                {expanded && (
                    <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-gray-800 truncate">
                            {user?.firstname} {user?.lastname}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                            {facility?.name || t('user_menu.my_account')}
                        </p>
                    </div>
                )}
            </button>

            {isOpen && (
                <div
                    className={`absolute bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 min-w-[280px] ${
                        expanded
                            ? "bottom-full mb-1 left-0"
                            : "left-full ml-2 bottom-0"
                    }`}
                >
                    {/* Section Emails */}
                    {facility && (
                        <>
                            {/* Emails mensuels */}
                            <div className="px-4 py-2">
                                <div className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                                    <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                                    <span>
                                        {t('user_menu.emails_monthly', {
                                            remaining: facility.current_monthly_email_consumption ?? 0,
                                            limit: facility.email_limit ?? 0,
                                        })}
                                    </span>
                                </div>
                                <div className="overflow-hidden rounded-full bg-gray-200 h-2">
                                    <div
                                        style={{ width: `${Math.min(emailRemainingPercentage, 100)}%` }}
                                        className={`h-2 rounded-full ${
                                            unusedKeys.has('email')
                                                ? 'bg-gray-400'
                                                : getEmailProgressColor(emailRemainingPercentage)
                                        }`}
                                    />
                                </div>
                                {unusedKeys.has('email') && (
                                    <p className="mt-1 text-xs text-gray-400">{t('consumables.not_in_use')}</p>
                                )}
                            </div>

                            {/* Crédits emails additionnels */}
                            <div className="px-4 py-2 flex items-center gap-2 text-sm text-gray-700">
                                <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                                <span>
                                    {t('user_menu.emails_credit', { count: facility.email_credit ?? 0 })}
                                </span>
                            </div>

                            {/* Crédit SMS */}
                            <div className="px-4 py-2">
                                <div className="flex items-center gap-2 text-sm text-gray-700">
                                    <ChatBubbleLeftIcon className="w-4 h-4 text-gray-400" />
                                    <span>
                                        {t('user_menu.sms_credit', {
                                            amount: facility.sms_credit ? facility.sms_credit / 100 : 0,
                                        })}
                                    </span>
                                </div>
                                {/* La mention explique le silence des alertes. Sans elle, la
                                    première question au support est « pourquoi je n'ai rien
                                    reçu alors que mon crédit est à zéro ? », à laquelle
                                    personne ne peut répondre sans lire le code du scan. */}
                                {unusedKeys.has('sms') && (
                                    <p className="mt-1 ml-6 text-xs text-gray-400">{t('consumables.not_in_use')}</p>
                                )}
                            </div>

                            <div className="border-t border-gray-200 my-2" />
                        </>
                    )}

                    {/* Section IA */}
                    {aiUsage && !aiUsage.is_unlimited && (
                        <>
                            <div className="px-4 py-2">
                                <div className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                                    <SparklesIcon className="w-4 h-4 text-gray-400" />
                                    <span>
                                        {t('user_menu.ai_credits', {
                                            remaining: aiUsage.credits_remaining,
                                            limit: aiUsage.credits_limit,
                                        })}
                                    </span>
                                </div>
                                <div className="overflow-hidden rounded-full bg-gray-200 h-2">
                                    <div
                                        style={{ width: `${aiRemainingPercentage}%` }}
                                        className={`h-2 rounded-full ${
                                            unusedKeys.has('ai')
                                                ? 'bg-gray-400'
                                                : getUsageBgColor(aiUsage.percentage_used)
                                        }`}
                                    />
                                </div>
                                {unusedKeys.has('ai') && (
                                    <p className="mt-1 text-xs text-gray-400">{t('consumables.not_in_use')}</p>
                                )}
                            </div>
                            <div className="border-t border-gray-200 my-2" />
                        </>
                    )}

                    {aiUsage?.is_unlimited && (
                        <>
                            <div className="px-4 py-2 flex items-center gap-2 text-sm text-purple-600">
                                <SparklesIcon className="w-4 h-4" />
                                <span className="font-medium">{t('user_menu.ai_unlimited')}</span>
                            </div>
                            <div className="border-t border-gray-200 my-2" />
                        </>
                    )}

                    {/* Informations utilisateur */}
                    <div className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900 truncate">
                            {user.firstname} {user.lastname}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                            {user.email}
                        </p>
                    </div>

                    <div className="border-t border-gray-200 my-1" />

                    {/* Sélecteur de langue */}
                    <UserLocaleSwitcher />

                    {/* Sécurité du compte (2FA, appareils, déconnexion de tous les appareils) :
                        page des Paramètres, ouverte à tous les comptes, y compris sans accès
                        aux réglages d'établissement. */}
                    <Link
                        to={ACCOUNT_SECURITY_LINK}
                        onClick={() => setIsOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <ShieldCheckIcon className="w-4 h-4 text-gray-400" />
                        <span>{t('user_menu.account_security')}</span>
                    </Link>

                    <div className="border-t border-gray-200 my-1" />

                    {/* Bouton Déconnexion */}
                    <button
                        onClick={logout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <ArrowLeftEndOnRectangleIcon className="w-4 h-4 text-gray-400" />
                        <span>{t('user_menu.logout')}</span>
                    </button>
                </div>
            )}
        </div>
    );
}
